import { observable } from "@legendapp/state";

import { createDatasetFolder, datasetFolders$ } from "~/stores/dataset-folders";
import { datasets$ } from "~/stores/datasets";
import { datasources$ } from "~/stores/datasources";

/** Droppable id used for the trailing "Unfiled" section. */
export const UNFILED_SECTION_ID = "unfiled";

/** Case-insensitive name used for sorting; unnamed datasets sort last. */
const sortName = (id: string): string =>
  (datasets$[id].name.get() || "\uffff").toLowerCase();

const byName = (a: string, b: string): number =>
  sortName(a).localeCompare(sortName(b));

/**
 * True when the dataset matches the current search query. Matches on the
 * dataset name and its datasource name so "postgres" finds everything built on
 * that connection.
 */
const matchesSearch = (id: string, query: string): boolean => {
  if (!query) return true;
  const name = datasets$[id].name.get() ?? "";
  if (name.toLowerCase().includes(query)) return true;
  const datasourceId = datasets$[id].datasourceId.get();
  if (!datasourceId) return false;
  const datasourceName = datasources$[datasourceId].name.get() ?? "";
  return datasourceName.toLowerCase().includes(query);
};

/** Local UI state for the datasets browse page. */
export const datasetsPage$ = observable({
  search: "",
  /** Whether private (unpublished) drafts are listed alongside published datasets. */
  showDrafts: true,
  /** Section ids the user has collapsed, keyed by folder id or `UNFILED_SECTION_ID`. */
  collapsedSections: {} as Record<string, boolean>,
  /** Folder currently being renamed inline. */
  renamingFolderId: null as string | null,

  /** True while a search query is active — sections flatten into one list. */
  isSearching: (): boolean => datasetsPage$.search.get().trim().length > 0,

  /**
   * Whether dragging a row can actually file it somewhere. The drop targets are
   * the folder sections, which only render for an org that has folders and only
   * while the list is grouped — a search flattens it. Without one on screen a
   * drag is an affordance leading nowhere, so rows stop being draggable.
   */
  canFileByDrag: (): boolean =>
    !datasetsPage$.isSearching.get() &&
    datasetsPage$.folderIds.get().length > 0,

  /** Dataset ids passing the search + drafts filters, sorted by name. */
  filteredIds: (): string[] => {
    const query = datasetsPage$.search.get().trim().toLowerCase();
    const showDrafts = datasetsPage$.showDrafts.get();

    return Object.keys(datasets$)
      .filter((id) => {
        if (!showDrafts && datasets$[id].private.get()) return false;
        return matchesSearch(id, query);
      })
      .sort(byName);
  },

  /**
   * Folder ids in `order`. Kept separate from the datasets in each folder so a
   * rename or a filter change re-renders one section rather than the page.
   */
  folderIds: (): string[] =>
    Object.values(datasetFolders$.get() ?? {})
      .filter((folder): folder is NonNullable<typeof folder> => folder != null)
      .sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0))
      .map((folder) => folder.id),
});

/**
 * The filtered datasets filed under `folderId` (`null` = unfiled), in name
 * order. Reads are tracked, so callers can wrap this in `useValue`.
 */
export const filteredDatasetIdsInFolder = (folderId: string | null): string[] =>
  datasetsPage$.filteredIds
    .get()
    .filter((id) => (datasets$[id].folderId.get() ?? null) === folderId);

/** Creates a folder, drops straight into inline rename, and returns its id. */
export const createNamedDatasetFolder = (): string => {
  const folderId = createDatasetFolder();
  datasetsPage$.renamingFolderId.set(folderId);
  return folderId;
};

/** Sets the search query. */
export const setDatasetSearch = (value: string) => {
  datasetsPage$.search.set(value);
};

/** Collapses or expands a folder section. */
export const toggleDatasetSection = (sectionId: string) => {
  datasetsPage$.collapsedSections[sectionId].set((collapsed) => !collapsed);
};

/** Droppable id for a folder section (`null` = the unfiled bucket). */
export const sectionDropId = (folderId: string | null): string =>
  `section:${folderId ?? UNFILED_SECTION_ID}`;

/**
 * Resolves a droppable id back to a folder id. Returns `undefined` when the id
 * is not a section drop target.
 */
export const folderIdFromDropId = (
  dropId: string,
): string | null | undefined => {
  if (!dropId.startsWith("section:")) return undefined;
  const suffix = dropId.slice("section:".length);
  return suffix === UNFILED_SECTION_ID ? null : suffix;
};
